import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users, User } from 'lucide-react';
import useStore from '@/store/useStore';
import { Team } from '@/types/cricket';

const TeamDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { teams, players, fetchTeams, fetchPlayers } = useStore();
  const [team, setTeam] = useState<Team | null>(null);

  useEffect(() => {
    fetchTeams();
    fetchPlayers();
  }, [fetchTeams, fetchPlayers]);

  useEffect(() => {
    if (teams.length > 0 && id) {
      const foundTeam = teams.find(t => t.id === id);
      setTeam(foundTeam || null);
    }
  }, [teams, id]);

  if (!team) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Team not found</h2>
        <Link to="/teams">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Teams
          </Button>
        </Link>
      </div>
    );
  }

  const teamPlayers = players.filter(p => Array.isArray(p.teamIds) && p.teamIds.includes(team.id));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Link to="/teams">
          <Button variant="outline" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold">{team.name}</h1>
          {team.shortCode && (
            <span className="text-sm font-bold bg-muted px-2 py-1 rounded">
              {team.shortCode}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Players</span>
              <span className="font-semibold">{teamPlayers.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Team Color</span>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full border" 
                  style={{ backgroundColor: team.color || '#10b981' }}
                ></div>
                <span className="text-sm">{team.color || 'Default'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Team Players ({teamPlayers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamPlayers.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {teamPlayers.map((player) => (
                  <div key={player.id} className="p-3 border rounded-lg hover:bg-accent/50">
                    <div className="font-medium">{player.name}</div>
                    <div className="text-sm text-muted-foreground">{player.role}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No players in this team yet.
                <div className="mt-2">
                  <Link to="/players/new">
                    <Button size="sm">Add Players</Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamDetailPage;
